import AsyncStorage from '@react-native-async-storage/async-storage'
import superjson from 'superjson'
import { create, StoreApi, UseBoundStore } from 'zustand'
import {
  devtools,
  persist,
  PersistStorage,
  StorageValue,
} from 'zustand/middleware'

import {
  AdvancedFilterSlice,
  createAdvancedFilterSlice,
} from '@/store/advancedFilterSlice'
import { AppTourSlice, createAppTourSlice } from '@/store/appTourSlice'
import { AuthSlice, createAuthSlice } from '@/store/authStore'
import {
  BasicFiltersSlice,
  createBasicFilterSlice,
} from '@/store/basicFilterSlice'
import { BulkSelectSlice, createBulkSelectSlice } from '@/store/bulkSelectSlice'
import { createGlobalTaskSlice, GlobalTaskSlice } from '@/store/globalTaskSlice'
import {
  createOrganizationSlice,
  OrganizationSlice,
} from '@/store/organizationStore'
import {
  createOrganizationSwitcherSlice,
  OrganizationSwitcherSlice,
} from '@/store/organizationSwitcherSlice'
import { createPushTokenSlice, PushTokenSlice } from '@/store/pushTokenSlice'
import { createUsersSlice, UsersSlice } from '@/store/usersStore'

type AppStoreState = AdvancedFilterSlice &
  AppTourSlice &
  AuthSlice &
  BasicFiltersSlice &
  BulkSelectSlice &
  GlobalTaskSlice &
  OrganizationSlice &
  OrganizationSwitcherSlice &
  PushTokenSlice &
  UsersSlice

type WithSelectors<S> = S extends { getState: () => infer T }
  ? S & { use: { [K in keyof T]: () => T[K] } }
  : never

const createSelectors = <S extends UseBoundStore<StoreApi<object>>>(
  _store: S
) => {
  const store = _store as WithSelectors<typeof _store>
  store.use = {}
  for (const k of Object.keys(store.getState())) {
    ;(store.use as any)[k] = () => store((s) => s[k as keyof typeof s])
  }

  return store
}

const storage: PersistStorage<AppStoreState> = {
  getItem: async (name: string) => {
    const item = await AsyncStorage.getItem(name)
    return item ? superjson.parse(item) : null
  },
  setItem: async (name: string, value: StorageValue<AppStoreState>) => {
    await AsyncStorage.setItem(name, superjson.stringify(value))
  },
  removeItem: async (name: string) => {
    await AsyncStorage.removeItem(name)
  },
}

const useAppStoreBase = create<AppStoreState>()(
  devtools(
    persist(
      (set, ...rest) => ({
        ...createAdvancedFilterSlice(set, ...rest),
        ...createAppTourSlice(set, ...rest),
        ...createAuthSlice(set, ...rest),
        ...createBasicFilterSlice(set, ...rest),
        ...createBulkSelectSlice(set, ...rest),
        ...createGlobalTaskSlice(set, ...rest),
        ...createOrganizationSlice(set, ...rest),
        ...createOrganizationSwitcherSlice(set, ...rest),
        ...createPushTokenSlice(set, ...rest),
        ...createUsersSlice(set, ...rest),
      }),
      {
        name: 'app-store',
        partialize: (state) => {
          // eslint-disable-next-line no-lone-blocks
          {
            // We strip out state that should not be persisted across app restarts
            const { auth, bulkSelect, globalTasks, ...rest } = state
            return rest
          }
        },
        // @ts-expect-error this is mad because of the partialize above, but we're following the docs and it works, so 🤷‍♂️
        storage: storage,
        version: 1,
        // Version 0 could persist an org without subscriptionActive, which reads as a lapsed
        // subscription. Treat it like a newly selected org: paid up until the check says otherwise.
        migrate: (persistedState) => {
          const state = persistedState as AppStoreState
          const organization = state.currentOrganization?.organization
          if (organization && organization.subscriptionActive === undefined) {
            organization.subscriptionActive = true
          }
          return state
        },
      }
    )
  )
)

// eslint-disable-next-line import/prefer-default-export
export const useAppStore = createSelectors(useAppStoreBase)
